import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { FileText, Plus, IndianRupee, ClipboardList, CheckCircle2, Clock, Search, Download, CalendarIcon, X, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_challans: 0, paid: 0, unpaid: 0, total_collected: 0 });
  const [challans, setChallans] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [exporting, setExporting] = useState(false);

  const isAdmin = user?.role === 'admin';

  const buildParams = useCallback(() => {
    const params = {};
    if (dateFrom) params.date_from = format(dateFrom, 'yyyy-MM-dd');
    if (dateTo) params.date_to = format(dateTo, 'yyyy-MM-dd');
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    return params;
  }, [dateFrom, dateTo, statusFilter]);

  const fetchData = useCallback(async () => {
    try {
      const params = buildParams();
      const [statsRes, challansRes] = await Promise.all([
        axios.get(`${API}/challans/stats`, { withCredentials: true, params: { date_from: params.date_from, date_to: params.date_to } }),
        axios.get(`${API}/challans`, { withCredentials: true, params })
      ]);
      setStats(statsRes.data);
      setChallans(challansRes.data);
    } catch (e) {
      console.error('Failed to fetch dashboard data', e);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = buildParams();
      const response = await axios.get(`${API}/challans/export/csv`, {
        withCredentials: true,
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `challans_export_${format(new Date(), 'yyyyMMdd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setDateFrom(null);
    setDateTo(null);
    setStatusFilter('all');
  };

  const hasFilters = dateFrom || dateTo || statusFilter !== 'all';

  const filtered = challans.filter(c =>
    c.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.owner_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.challan_number?.toLowerCase().includes(search.toLowerCase()) ||
    c.mobile_number?.includes(search) ||
    c.business_id?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse text-slate-400">Loading dashboard...</div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Welcome back, {user?.name || 'Officer'}
            {isAdmin && <span className="ml-2 inline-flex"><Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]" variant="outline">Admin</Badge></span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleExportCSV}
            disabled={exporting}
            variant="outline"
            data-testid="export-csv-btn"
            className="border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold h-11 transition-all duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Link to="/challan/new">
            <Button data-testid="new-challan-btn" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold tracking-wide h-11 px-6 transition-all duration-200">
              <Plus className="w-4 h-4 mr-2" />
              New Challan
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger-children" data-testid="stats-grid">
        <div className="border border-slate-200 p-5 rounded-lg hover:-translate-y-1 hover:shadow-sm transition-all duration-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total Challans</span>
            <ClipboardList className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-slate-900" data-testid="stat-total">{stats.total_challans}</p>
        </div>
        <div className="border border-slate-200 p-5 rounded-lg hover:-translate-y-1 hover:shadow-sm transition-all duration-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Paid</span>
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold text-emerald-600" data-testid="stat-paid">{stats.paid}</p>
        </div>
        <div className="border border-slate-200 p-5 rounded-lg hover:-translate-y-1 hover:shadow-sm transition-all duration-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Unpaid</span>
            <Clock className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-3xl font-bold text-amber-600" data-testid="stat-unpaid">{stats.unpaid}</p>
        </div>
        <div className="border border-slate-200 p-5 rounded-lg hover:-translate-y-1 hover:shadow-sm transition-all duration-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-500 uppercase tracking-wider">Collected</span>
            <IndianRupee className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-slate-900" data-testid="stat-collected">
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(stats.total_collected)}
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="filters-bar">
        <div className="flex items-center gap-1.5 text-sm text-slate-500 font-medium">
          <Filter className="w-4 h-4" />
          Filters
        </div>

        {/* Date From */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-testid="date-from-btn"
              className={`h-9 text-sm font-normal justify-start ${dateFrom ? 'text-slate-900' : 'text-slate-500'} border-slate-200`}
            >
              <CalendarIcon className="w-3.5 h-3.5 mr-2 text-slate-400" />
              {dateFrom ? format(dateFrom, 'dd MMM yyyy') : 'From date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateFrom}
              onSelect={setDateFrom}
              disabled={(date) => dateTo ? date > dateTo : false}
              initialFocus
              data-testid="date-from-calendar"
            />
          </PopoverContent>
        </Popover>

        {/* Date To */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-testid="date-to-btn"
              className={`h-9 text-sm font-normal justify-start ${dateTo ? 'text-slate-900' : 'text-slate-500'} border-slate-200`}
            >
              <CalendarIcon className="w-3.5 h-3.5 mr-2 text-slate-400" />
              {dateTo ? format(dateTo, 'dd MMM yyyy') : 'To date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateTo}
              onSelect={setDateTo}
              disabled={(date) => dateFrom ? date < dateFrom : false}
              initialFocus
              data-testid="date-to-calendar"
            />
          </PopoverContent>
        </Popover>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="status-filter" className="w-36 h-9 text-sm border-slate-200">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            data-testid="clear-filters-btn"
            className="text-slate-500 hover:text-red-600 h-9 text-xs font-medium"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Table Section */}
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid="challans-table-container">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            {isAdmin ? 'All Challans' : 'Your Challans'}
            <span className="text-sm font-normal text-slate-400 ml-2">({filtered.length})</span>
          </h2>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="search-challans"
              placeholder="Search by name, number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-50 border-slate-200 h-9"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No challans found</p>
            <p className="text-sm mt-1">{hasFilters ? 'Try adjusting your filters' : 'Create your first challan to get started'}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Challan #</TableHead>
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Business ID</TableHead>
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Business</TableHead>
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Owner</TableHead>
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Mobile</TableHead>
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Amount</TableHead>
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Status</TableHead>
                {isAdmin && <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Officer</TableHead>}
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Date</TableHead>
                <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`challan-row-${c.id}`} className="hover:bg-slate-50/50">
                  <TableCell className="font-mono text-xs text-blue-600">{c.challan_number}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{c.business_id || '-'}</TableCell>
                  <TableCell className="font-medium text-slate-900">{c.business_name}</TableCell>
                  <TableCell className="text-slate-600">{c.owner_name}</TableCell>
                  <TableCell className="text-slate-500 text-xs">{c.mobile_number}</TableCell>
                  <TableCell className="font-semibold text-slate-900">
                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(c.amount)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      data-testid={`challan-status-${c.id}`}
                      className={c.payment_status === 'paid'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                        : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                      }
                      variant="outline"
                    >
                      {c.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-slate-600 text-sm">{c.officer_name || '-'}</TableCell>
                  )}
                  <TableCell className="text-slate-500 text-sm">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Link to={`/challan/${c.id}`}>
                      <Button variant="ghost" size="sm" data-testid={`view-challan-${c.id}`} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 text-xs font-semibold">
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
